/* eslint-disable no-underscore-dangle */
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import MarkdownIt from 'markdown-it';
import striptags from 'striptags';
import dayjs from '../../dayjs/dayjs_timezone';
import {
  getDepositPrice,
  interpolatePriceToTemplate,
  isDefaultTitle,
  priceFormat,
  renderFieldLabel,
} from '../utilities/helperUtility';
import { capitalize } from '../utilities/stringUtility';
import { cartTimeFormat, timeFormat, timeFormatFromParams } from '../utilities/timeUtility';

const { dateFormat, timeZone } = window.bta;

const correctKey = (key) => {
  if (key === '_start_with_time') return 'Start';
  if (key === '_finish_with_time') return 'Finish';
  return key;
};

const orderTimeZone = (order) => {
  const { selectedLocation } = order;
  return selectedLocation ? selectedLocation.timezone : timeZone;
};

const md = new MarkdownIt();

export const getValue = ({ order, settings: { setting }, product: { data: product } }, item, key, isRental) => ({
  Date() {
    return dayjs(item.start).tz(orderTimeZone(order)).format(`${dateFormat}`);
  },
  Time() {
    let timeFormat = cartTimeFormat();
    if (setting?.time_format) {
      timeFormat = timeFormatFromParams(window.bta.locale, setting.time_format).replace('h', 'hh');
    }
    return dayjs(item.start).tz(orderTimeZone(order)).format(timeFormat).replace(/\s/g, '');
  },
  Start() {
    const format = isRental ? `${dateFormat}` : `${dateFormat} ${cartTimeFormat()}`;
    return dayjs(item.start).tz(orderTimeZone(order)).format(format);
  },
  _start_with_time() {
    return dayjs(item.start).tz(orderTimeZone(order)).format(`${dateFormat} ${cartTimeFormat()}`);
  },
  Finish() {
    const format = isRental ? `${dateFormat}` : `${dateFormat} ${cartTimeFormat()}`;
    return dayjs(item.finish).tz(orderTimeZone(order)).format(format);
  },
  _finish_with_time() {
    return dayjs(item.finish).tz(orderTimeZone(order)).format(`${dateFormat} ${cartTimeFormat()}`);
  },
  Term() {
    return order.termSelected.name;
  },
  Resource() {
    const { resources = [] } = product;
    if (order.selectedResource === null || resources.length === 1) return null;
    return order.selectedResource.name;
  },
  _resource() {
    const { resources = [] } = product;
    if (order.selectedResource === null || resources.length === 1) return null;
    return order.selectedResource.name;
  },
  _resource_ids() {
    const { resources = [] } = product;
    if (order.selectedResource === null || resources.length === 1) return null;
    return order.selectedResource.id.toString();
  },
  Location() {
    if (order.selectedLocation === null) return null;
    return order.selectedLocation.name;
  },
  Duration() {
    return `\n${order.dateRange.duration} Days`;
  },
  Units() {
    if (order.variantSelected === null) return null;
    const selectedVariant = product.variants.find((v) => v.external_id === order.variantSelected) || {};
    return selectedVariant.unit_multiplier;
  },
}[key]());

export const getISO8601FormattedKeyValueTuple = (key, item) => {
  const isoKey = `_${key.toLowerCase()}_iso8601`;
  let isoValue = null;
  if (key === 'Date' || key === 'Start') {
    isoValue = item.start;
  } else if (key === 'Finish') {
    isoValue = item.finish;
  }
  return [isoKey, isoValue];
};

const convertDateTime = (value, dFormat, timeZone) => {
  if (!value) return value;
  return dayjs.utc(value).tz(timeZone).format(dFormat);
};

const assignPropertyValue = (properties, label, value, name, options) => {
  const { fm, tz } = options;
  const propertiesLabel = striptags(renderFieldLabel(label, md).replace('\n', ''));

  let temp = '';
  if (name === 'date_time') {
    temp = Array.isArray(value) ? value.map((v) => (v ? convertDateTime(v, fm, tz) : '')).join(', ') : convertDateTime(value, fm, tz);
  } else {
    temp = Array.isArray(value) ? value.join(', ') : value;
  }
  properties[propertiesLabel] = temp;
};

const getNonRepeatedFieldValue = (properties, fieldsSetting, options) => {
  const { additionalDetails, bookingFields } = fieldsSetting;

  bookingFields.filter((field) => !field.repeated).forEach((field) => {
    const { label, name } = field;
    const value = additionalDetails[field.key];
    if (value) assignPropertyValue(properties, label, value, name, options);
  });

  return properties;
};

const getRepeatedFieldValue = (properties, fieldsSetting, options) => {
  const { additionalDetails, bookingFields } = fieldsSetting;

  const repeatedFields = bookingFields.filter((field) => field.repeated).map((field) => {
    const { label, name } = field;
    const values = additionalDetails[field.key] || [];
    return { label, values, name };
  });

  if (repeatedFields.length === 0) return properties;

  repeatedFields[0].values.forEach((_v, index) => {
    repeatedFields.forEach((field) => {
      const { label, values, name } = field;
      const fieldLabel = index === 0 ? label : `${label}-${index + 1}`;
      assignPropertyValue(properties, fieldLabel, values[index], name, options);
    });
  });

  return properties;
};

export const getAdditionalFieldValue = (properties, fieldsSetting, order = null) => {
  const fm = `${dateFormat} ${timeFormat()}`;
  const tz = order ? orderTimeZone(order) : timeZone;
  getNonRepeatedFieldValue(properties, fieldsSetting, { fm, tz });
  getRepeatedFieldValue(properties, fieldsSetting, { fm, tz });
  return properties;
};

export const addItems = (_reservation, type = [], fieldsSetting = {}, options = {}) => (_dispatch, getState) => {
  const { onSubmit, onTrigger } = window.xprops;
  const { isRental, quantityRange, visibleResource } = options;

  const state = getState();
  const {
    product: {
      data: {
        resources = [],
        variants,
        external_id: product_external_id,
      },
    },
    order,
    order: {
      selectedResource,
      variantSelected,
      variantsSelected,
      paymentOption,
    },
    settings,
    settings: {
      setting: {
        name: widgetType,
      },
    },
  } = state;

  const reservationItems = [];
  const isPayInFull = paymentOption !== 'payDeposit';

  _reservation.items.forEach((item) => {
    const properties = {};

    type.forEach((_key) => {
      const key = capitalize(_key);
      const value = getValue(state, item, key, isRental);
      if (value) {
        properties[correctKey(key)] = value;

        const [isoKey, isoValue] = getISO8601FormattedKeyValueTuple(key, item);

        if (isoValue) properties[isoKey] = isoValue;
      }
    });

    if (isRental && quantityRange) properties.Units = item.quantity;
    if (!isEmpty(fieldsSetting)) getAdditionalFieldValue(properties, fieldsSetting, order);
    if (resources.length > 1 && !selectedResource?.id) {
      if (visibleResource) {
        properties.Resource = item.resource_name;
      } else {
        properties._resource = item.resource_name;
      }
    }

    if (item?.resources_ids?.length > 1) properties._resource_ids = item.resources_ids.join(', ');

    const {
      variant_title, external_id, deposit_external_id, security_bond_external_id, quantity,
    } = item;
    const selectedVariant = variants.find((v) => {
      if (widgetType === 'Activity') {
        return variantsSelected.includes(v.external_id) && v.external_id === external_id;
      }
      return v.external_id === variantSelected;
    });
    const totalPrice = selectedVariant?.price || 0;

    let productTitle = item.product_title;
    if (!isDefaultTitle(variant_title)) productTitle += `/${variant_title}`;

    const itemQuantity = isRental && quantityRange ? quantityRange * quantity : quantity;

    if (deposit_external_id && deposit_external_id !== external_id && !isPayInFull) {
      const depositPrice = getDepositPrice(settings.depositSettings, totalPrice);
      properties._product_id = product_external_id;
      properties._variant_id = external_id;
      properties._product_has_security_bond = !!security_bond_external_id;
      properties['Grand Total'] = interpolatePriceToTemplate(priceFormat(settings), totalPrice * itemQuantity);
      properties['Deposit Due Today'] = interpolatePriceToTemplate(priceFormat(settings), depositPrice * itemQuantity);
      properties.product = productTitle;
    }

    reservationItems.push({
      id: isPayInFull ? external_id : deposit_external_id || external_id,
      quantity: itemQuantity,
      properties,
    });

    if (security_bond_external_id) {
      const securityBondProperties = pick(properties, ['Date', 'Time', 'Start', 'Finish', 'product', '_variant_id']);
      securityBondProperties.product = productTitle;
      securityBondProperties._variant_id = external_id;

      // Add cart total for percentage-based security bond calculation
      if (settings.securityBondSettings?.bond_type === 'percentage') {
        securityBondProperties._cart_total = totalPrice * itemQuantity;
        securityBondProperties._bond_percentage = settings.securityBondSettings.percentage;
      }

      reservationItems.push({
        id: security_bond_external_id,
        quantity: itemQuantity,
        properties: securityBondProperties,
      });
    }
  });

  const reservation = {
    ..._reservation,
    items: reservationItems,
  };

  const newCartToken = reservation.items.length ? reservation.cart_token : null;

  return new Promise((resolve) => {
    onTrigger('updateCartToken', newCartToken, () => {
      if (onSubmit) {
        onSubmit(reservation, { widgetSettings: settings.setting });
      }
      resolve(reservation);
    });
  });
};
